import { useCallback } from "react";
import Skeleton from "react-loading-skeleton";
import { useHistory } from "react-router-dom";

import { DetailLayout } from "@/layouts/DetailLayout/DetailLayout";
import { useAuthContext } from "@/modules/auth/hooks/useAuthContext";
import { authenticatedRouting } from "@/modules/router/routings/authenticatedRouting";
import { DetailLayoutBox } from "@/modules/theme/components/DetailLayoutBox";
import { Loader } from "@/modules/theme/components/Loader/Loader";
import { PointsLabel } from "@/modules/theme/components/PointsLabel";
import { grey, yellow } from "@/modules/theme/utils/colors";
import { useUserDataContext } from "@/modules/user/hooks/useUserDataContext";

import { SubtaskRating } from "./components/SubtaskRating";
import { TaskStatusToggler } from "./components/TaskStatusToggler";
import { useSubtaskDetailsQuery } from "./hooks/useSubtaskDetailsQuery";
import { useUndoSubtaskCompletionMutation } from "./hooks/useUndoSubtaskCompletionMutation";
import { createSubtaskDetailsPageStyles } from "./styles/createSubtaskDetailsPageStyles";

export const DoneSubtaskDetailsPage = () => {
  const classes = createSubtaskDetailsPageStyles();
  const subtaskDetailsParams = authenticatedRouting.useRouteParams(
    "doneSubtaskDetails"
  );
  const history = useHistory();
  const { userId } = useAuthContext();
  const { currentGroup } = useUserDataContext();
  const {
    data: subtaskDetailsData,
    isFetching: areSubtaskDetailsFetching,
  } = useSubtaskDetailsQuery(subtaskDetailsParams?.id);
  const undoSubtaskCompletion = useUndoSubtaskCompletionMutation(() =>
    history.goBack()
  );

  const areRequestsPending =
    areSubtaskDetailsFetching || undoSubtaskCompletion.status === "loading";

  const handleUndoSubtaskCompletion = useCallback(() => {
    if (currentGroup && subtaskDetailsParams) {
      undoSubtaskCompletion.mutate({
        userGroupId: currentGroup.id,
        subtaskId: subtaskDetailsParams.id,
      });
    }
  }, [currentGroup, subtaskDetailsParams, undoSubtaskCompletion]);

  return (
    <DetailLayout
      variant="taskDone"
      heading={
        areSubtaskDetailsFetching ? (
          <Skeleton
            className={classes.headingSkeleton}
            baseColor={yellow[600]}
          />
        ) : (
          subtaskDetailsData?.name
        )
      }
      details={
        areSubtaskDetailsFetching ? (
          <Skeleton
            width={50}
            height={20}
            baseColor={grey[900]}
            highlightColor={grey[800]}
          />
        ) : (
          <PointsLabel points={subtaskDetailsData?.pointsNumber ?? 0} />
        )
      }
    >
      <DetailLayoutBox isTogglerVisible>
        {areSubtaskDetailsFetching ? (
          <div className={classes.loaderWrapper}>
            <Loader size={70} variant="primary" />
          </div>
        ) : (
          subtaskDetailsData && (
            <SubtaskRating
              rating={subtaskDetailsData.rating}
              comment={subtaskDetailsData.comment}
            />
          )
        )}
        <TaskStatusToggler
          done
          onButtonClick={() => handleUndoSubtaskCompletion()}
          isLoading={areRequestsPending}
          isDisabled={
            areRequestsPending || subtaskDetailsData?.complatedBy !== userId
          }
        />
      </DetailLayoutBox>
    </DetailLayout>
  );
};
